import type { ReactNode } from 'react';
import { ChevronRight, FileText, Hammer, PackageMinus, PackagePlus, QrCode } from 'lucide-react';
import { Badge, Button, Drawer, Skeleton, cn } from '@eombor/ui';
import type { FlowDetail, FlowLevel, FlowProduct, ReportRange, Unit } from '@eombor/contracts';
import { ApiErrorState } from '../../../shared/ui/ApiErrorState.js';
import { t } from '../../../shared/i18n/index.js';
import { formatMoment } from '../../../shared/format/moment.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { useFlowDetail } from '../model/flow.queries.js';
import type { FlowNode, FlowTarget } from '../api/flow.api.js';

/**
 * TUGUN DETALI — jadval satrining ortidagi javob.
 *
 * Jadval «qancha» ni aytadi: korxonada 12 kirim, 7 rasxod. Keyingi
 * savol esa darhol keladi va u BOSHQA savol — nima kirdi, nima chiqdi,
 * kim javondan olib ketdi va qaysi hujjat bilan. Jadvalga ustun qo'shib
 * javob berib bo'lmaydi: bu yerda ro'yxatlar bor, ustunda esa bitta
 * qiymat sig'adi.
 *
 * Nega modal emas: panel ortida jadval KO'RINIB turadi va rahbar qaysi
 * satrni bosganini ko'zdan yo'qotmaydi — u aynan satrlarni solishtirish
 * uchun keladi.
 */

const format = (value: number): string => value.toLocaleString('ru-RU');

/** Kasr uch xonaga yaxlitlanadi: `0.5 dona` mantiqiy, `0.5000001` emas. */
const qtyText = (qty: number): string =>
  (Math.round(qty * 1000) / 1000).toLocaleString('ru-RU');

/** Tugunning turi — sarlavha ostida «Korxona · PCH-800» bo'lib chiqadi. */
const LEVEL_KEY: Record<FlowLevel, string> = {
  MTU: 'stats.levelMtu',
  ENTERPRISE: 'stats.levelEnterprise',
  SUBDIVISION: 'stats.levelSubdivision',
  STOREROOM: 'stats.levelStoreroom',
};

/** Pastga tushish tugmasining matni — quyi darajaga qarab. */
const GO_KEY: Partial<Record<FlowLevel, string>> = {
  ENTERPRISE: 'stats.goEnterprise',
  SUBDIVISION: 'stats.goSubdivision',
  STOREROOM: 'stats.goStoreroom',
};

/**
 * Hujjat turining nomi.
 *
 * Tanilmagan tur O'Z HOLICHA chiqadi: yangi hujjat turi qo'shilganda
 * ekran bo'sh joy ko'rsatgandan ko'ra xom nomni ko'rsatgani yaxshi.
 */
const DOC_KEYS: Record<string, string> = {
  REQUISITION: 'stats.docRequisition',
  WRITE_OFF: 'stats.docWriteOff',
  RETURN: 'stats.docReturn',
  TRANSFER: 'stats.docTransfer',
  OPENING: 'stats.docOpening',
};

const docTypeText = (docType: string): string => {
  const key = DOC_KEYS[docType];
  return key === undefined ? docType : t(key);
};

type Tone = 'success' | 'info' | 'warn' | 'neutral';

const TILE: Record<Tone, string> = {
  success: 'bg-success-soft text-success',
  info: 'bg-info-soft text-info',
  warn: 'bg-warn-soft text-warn',
  neutral: 'bg-surface-2 text-text-muted',
};

/**
 * Ko'rsatkich plitasi.
 *
 * Birlik raqam OSTIDA alohida turadi va bu ataylab: «48» ning nima
 * ekani («hujjat»mi, «partiya»mi) raqamdan ko'ra muhimroq va u
 * raqamning kattaligi bilan raqobat qilmasligi kerak.
 */
const Tile = ({
  tone,
  label,
  value,
  unit,
}: {
  tone: Tone;
  label: string;
  value: number;
  unit: string;
}) => (
  <div className={cn('flex flex-col gap-1 rounded-[var(--radius-md)] px-3 py-2.5', TILE[tone])}>
    <span className="truncate text-xs font-medium">{label}</span>
    <span className="tabular text-2xl leading-none font-semibold text-text">{format(value)}</span>
    <span className="truncate text-[11px] text-text-faint">{unit}</span>
  </div>
);

const Section = ({
  icon,
  title,
  hint,
  children,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-2.5">
    <header className="flex items-baseline justify-between gap-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-text">
        <span aria-hidden className="text-text-faint">
          {icon}
        </span>
        {title}
      </h3>
      {hint === undefined ? null : <span className="text-xs text-text-faint">{hint}</span>}
    </header>
    {children}
  </section>
);

/**
 * Mahsulotlar ro'yxati — miqdor CHIZIQ bilan.
 *
 * Chiziqning uzunligi eng kattasiga nisbatan: «42 va 18» ni o'qib
 * solishtirish kerak, chiziqni esa ko'z o'zi solishtiradi. Birlik har
 * satrda o'zi bilan yuradi, chunki tugun ichida dona, metr va kilogramm
 * aralash bo'ladi — ularning yig'indisi yo'q.
 */
const Products = ({ rows, tone }: { rows: readonly FlowProduct[]; tone: 'success' | 'info' }) => {
  const max = rows.reduce((top, row) => Math.max(top, row.qty), 0);

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li key={row.nomenclatureId} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-3">
            <span className="min-w-0 flex-1 truncate text-sm text-text">{row.name}</span>
            <span className="tabular shrink-0 text-sm font-semibold text-text">
              {qtyText(row.qty)}{' '}
              <span className="text-xs font-normal text-text-muted">{unitText(row.unit as Unit)}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-[var(--radius-pill)] bg-surface-2"
            >
              <span
                className={cn(
                  'block h-full rounded-[var(--radius-pill)]',
                  tone === 'success' ? 'bg-success' : 'bg-info',
                )}
                /* Nisbat KODDA hisoblanadi: sinf nomi bilan 1%–100%
                   oralig'ini ifodalab bo'lmaydi. */
                style={{ width: `${max === 0 ? 0 : Math.max(4, (row.qty / max) * 100)}%` }}
              />
            </span>
            <span className="shrink-0 text-[11px] text-text-faint">
              {t('stats.inDocs', { count: row.docCount })}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};

/** Ism boshi — suratsiz ro'yxatda odamni bir-biridan ajratadi. */
const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

const Empty = ({ text }: { text: string }) => (
  <p className="rounded-[var(--radius-sm)] bg-surface-2 px-3 py-2.5 text-xs text-text-muted">
    {text}
  </p>
);

export interface FlowDetailPanelProps {
  range: ReportRange;
  /** `null` — panel yopiq va so'rov ketmaydi. */
  target: FlowTarget | null;
  onClose: () => void;
  onDrill: (node: FlowNode) => void;
}

export const FlowDetailPanel = ({ range, target, onClose, onDrill }: FlowDetailPanelProps) => {
  const query = useFlowDetail(range, target);
  const detail: FlowDetail | undefined = query.data;

  const goKey = target?.drillLevel === null ? undefined : GO_KEY[target?.drillLevel ?? 'MTU'];

  return (
    <Drawer
      open={target !== null}
      onClose={onClose}
      title={target?.name ?? ''}
      description={
        target === null
          ? undefined
          : [t(LEVEL_KEY[target.level]), detail?.code ?? null]
              .filter((part): part is string => part !== null && part !== '')
              .join(' · ')
      }
      footer={
        goKey === undefined || target === null || target.drillLevel === null ? undefined : (
          /* Pastga yo'l panel ICHIDA ham bor: rahbar raqamlarni ko'rib,
             «qaysi bo'linma?» deb so'raganda panelni yopib, satrni
             qaytib izlashi kerak bo'lmaydi. */
          <Button
            variant="secondary"
            onClick={() => {
              onDrill({ level: target.drillLevel as FlowLevel, parentId: target.id });
              onClose();
            }}
          >
            {t(goKey)}
            <ChevronRight size={16} aria-hidden />
          </Button>
        )
      }
    >
      {query.error !== null ? (
        <ApiErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : detail === undefined ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <Tile
              tone="success"
              label={t('stats.in')}
              value={detail.totals.inDocCount}
              unit={t('stats.unitDoc')}
            />
            <Tile
              tone="info"
              label={t('stats.out')}
              value={detail.totals.outDocCount}
              unit={t('stats.unitDoc')}
            />
            <Tile
              tone="warn"
              label={t('stats.inUse')}
              value={detail.totals.inUseLotCount}
              unit={t('stats.unitLot')}
            />
            <Tile
              tone="neutral"
              /* Qisqa yorliq: to'rtta plita yonma-yon turganda «Qoldiq
                 partiya» kesilib qolardi, birlik esa raqam ostida
                 allaqachon yozilgan. */
              label={t('stats.lotsShort')}
              value={detail.totals.lotCount}
              unit={t('stats.unitLot')}
            />
          </div>

          {/*
            RASXODNING TARKIBI — jadvalda uchalasi bitta raqamga yopishgan.
            Rahbar uchun ular bir xil emas: ishlatib tugatilgani normal
            ish, omborga qaytarilgani «ortiqcha olingan» degani, boshqa
            bo'linmaga berilgani esa yo'qotish emas.
          */}
          {detail.outSplit.length === 0 ? null : (
            <Section icon={<PackageMinus size={15} />} title={t('stats.outSplit')}>
              <ul className="flex flex-wrap gap-2">
                {detail.outSplit.map((part) => (
                  <li key={part.kind}>
                    <span className="flex items-center gap-1.5 rounded-[var(--radius-pill)] bg-surface-2 py-1 pr-3 pl-2.5 text-xs text-text-muted">
                      {t(`myStock.move.${part.kind}`)}
                      <span className="tabular font-semibold text-text">
                        {format(part.docCount)}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section icon={<PackagePlus size={15} />} title={t('stats.topIn')}>
            {detail.topIn.length === 0 ? (
              <Empty text={t('stats.noMoves')} />
            ) : (
              <Products rows={detail.topIn} tone="success" />
            )}
          </Section>

          <Section icon={<PackageMinus size={15} />} title={t('stats.topOut')}>
            {detail.topOut.length === 0 ? (
              <Empty text={t('stats.noMoves')} />
            ) : (
              <Products rows={detail.topOut} tone="info" />
            )}
          </Section>

          {/*
            QR BILAN OLISH — «kim ishlatyapti» degan savolning javobi.
            «Olgan» deb yoziladi, «ushlab turgan» deb emas: javonga
            qaytarish qayd etilmaydi va olingan mol keyinchalik rasxod
            aktida ko'rinadi.
          */}
          <Section
            icon={<QrCode size={15} />}
            title={t('stats.usageTitle')}
            hint={
              detail.usageTotal === 0
                ? undefined
                : t('stats.usageHint', {
                    workers: detail.totals.usageWorkerCount,
                    total: detail.usageTotal,
                  })
            }
          >
            {detail.usage.length === 0 ? (
              <Empty text={t('stats.noUsage')} />
            ) : (
              <ul className="flex flex-col gap-2.5">
                {detail.usage.map((row) => (
                  <li key={row.id} className="flex items-start gap-2.5">
                    <span
                      aria-hidden
                      className="tabular flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-pill)] bg-accent-soft text-[11px] font-semibold text-accent"
                    >
                      {initialsOf(row.workerName)}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="flex flex-wrap items-baseline justify-between gap-x-3">
                        <span className="truncate text-sm font-medium text-text">
                          {row.workerName}
                        </span>
                        <span className="tabular shrink-0 text-sm font-semibold text-text">
                          {qtyText(row.qty)}{' '}
                          <span className="text-xs font-normal text-text-muted">
                            {unitText(row.unit as Unit)}
                          </span>
                        </span>
                      </span>
                      <span className="truncate text-xs text-text-muted">
                        {[row.position, row.nomenclatureName, row.storeroomName]
                          .filter((part): part is string => part !== null && part !== '')
                          .join(' · ')}
                      </span>
                      <span className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-text-faint">
                        <span className="tabular">{formatMoment(row.usedAt)}</span>
                        <span className="min-w-0 truncate">{row.comment}</span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            icon={<FileText size={15} />}
            title={t('stats.docsTitle')}
            hint={
              detail.docTotal === 0 ? undefined : t('stats.docsHint', { total: detail.docTotal })
            }
          >
            {detail.docs.length === 0 ? (
              <Empty text={t('stats.noDocs')} />
            ) : (
              <ul className="flex flex-col gap-2">
                {detail.docs.map((doc) => (
                  <li
                    key={`${doc.docType}:${doc.docId}`}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1"
                  >
                    <Badge tone={doc.direction === 'IN' ? 'success' : 'info'}>
                      {doc.direction === 'IN' ? t('stats.dirIn') : t('stats.dirOut')}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate text-sm text-text">
                      {/* Raqami yo'q hujjat TURI bilan qoladi: bo'sh joy
                          «hujjat yo'qoldi» degan taassurot berardi. */}
                      {doc.docNo ?? docTypeText(doc.docType)}
                      <span className="ml-2 text-xs text-text-faint">
                        {t('stats.docLines', { count: doc.lineCount })}
                      </span>
                    </span>
                    <span className="tabular shrink-0 text-xs text-text-faint">
                      {formatMoment(doc.at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/*
            ISHLATISHDAGI mol haqida ochiq gap. Panel raqamni ko'rsatadi,
            lekin «kim hozir ushlab turgan» ni ayta olmaydi: javonga
            qaytarish oqimi yo'q. Buni aytmaslik raqamni tushunarsiz
            qilardi va rahbar undan noto'g'ri xulosa chiqarardi.
          */}
          {detail.totals.inUseLotCount === 0 ? null : (
            <p className="flex items-start gap-2 rounded-[var(--radius-sm)] bg-surface-2 px-3 py-2.5 text-xs text-text-muted">
              <Hammer size={14} aria-hidden className="mt-0.5 shrink-0 text-text-faint" />
              {t('stats.inUseNote')}
            </p>
          )}
        </div>
      )}
    </Drawer>
  );
};
